import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CircleAlert } from "lucide-react";
import {
  useIsBitbucketConnected,
  useIsGithubConnected,
  useIsGitlabConnected,
} from "@/utils/utils";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { useClerk } from "@clerk/clerk-react";

interface NoRepositoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateRepository: (provider: "github" | "gitlab" | "bitbucket") => void;
}

const NoRepositoryModal: React.FC<NoRepositoryModalProps> = ({
  isOpen,
  onClose,
  onCreateRepository,
}) => {
  const { buildUserProfileUrl } = useClerk();

  const handleCreateRepoClick = (
    provider: "github" | "gitlab" | "bitbucket"
  ) => {
    onClose();
    onCreateRepository(provider);
  };

  const handleConnectGitClick = () => {
    onClose();
    window.open(buildUserProfileUrl(), "_blank");
  };

  const isGithubConnected = useIsGithubConnected();
  const isGitlabConnected = useIsGitlabConnected();
  const isBitbucketConnected = useIsBitbucketConnected();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-background-primary xs:max-w-[500px] overflow-hidden rounded-lg border border-border-primary p-0">
        <div className="flex flex-col">
          {!isGithubConnected && !isGitlabConnected ? (
            // Case: No Git providers connected
            <>
              <div className="flex justify-between items-center p-4 pb-0">
                <span className="text-text-primary font-medium">
                  Connect a Git Provider
                </span>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-background-secondary p-4 rounded-md border border-border-secondary flex items-start space-x-3">
                  <CircleAlert className="max-w-4 max-h-4 min-w-4 min-h-4 text-icons-alert" />
                  <div>
                    <p className="text-text-primary font-medium text-xs">
                      No Git provider is connected
                    </p>
                    <p className="text-text-secondary text-xs mt-1">
                      Your canvases stay in sync through Git. Connect a Git
                      provider to keep your work connected and up to date.{" "}
                      <br />
                      <a
                        href="#"
                        className="text-primary-0 hover:underline text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            "https://tempolabsinc.mintlify.app/Basics/Git",
                            "_blank"
                          );
                        }}
                      >
                        Learn more
                      </a>
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-text-primary text-xs mb-2">
                    Connect to a Git Provider through your account settings.
                  </p>
                  <Button
                    onClick={handleConnectGitClick}
                    className="w-full text-xs bg-primary-0 hover:bg-primary-100 text-text-on-primary rounded-md py-2.5"
                  >
                    Open Account Settings
                  </Button>
                </div>
              </div>
            </>
          ) : (
            // Case: Git provider(s) connected
            <>
              <div className="flex justify-between items-center p-4 pb-0">
                <span className="text-text-primary font-medium">
                  Choose Git Provider
                </span>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-background-secondary p-4 rounded-md border border-border-secondary flex items-start space-x-3">
                  <CircleAlert className="max-w-4 max-h-4 min-w-4 min-h-4 text-icons-alert" />
                  <div>
                    <p className="text-text-primary font-medium text-xs">
                      No repository is connected
                    </p>
                    <p className="text-text-secondary text-xs mt-1">
                      Your canvases stay in sync through Git. Create a Git
                      repository to keep your work connected and up to date.{" "}
                      <br />
                      <a
                        href="#"
                        className="text-primary-0 hover:underline text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            "https://tempolabsinc.mintlify.app/Basics/Git",
                            "_blank"
                          );
                        }}
                      >
                        Learn more
                      </a>
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-text-primary text-xs mb-2">
                    Choose a provider, then select an org and name your repo.
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-html={
                        !isGithubConnected
                          ? "A GitHub account is required to create a repository.<br /><br />Please go to your User Settings to connect your GitHub account."
                          : ""
                      }
                    >
                      <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center h-14 bg-background-secondary hover:bg-background-tertiary border-border-secondary w-full"
                        onClick={() => handleCreateRepoClick("github")}
                        disabled={!isGithubConnected}
                      >
                        <GitHubLogoIcon className="w-6 h-6 mb-1" />
                        <span className="text-text-primary text-xs">
                          GitHub
                        </span>
                      </Button>
                    </span>
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-html={
                        !isGitlabConnected
                          ? "A GitLab account is required to create a repository.<br /><br />Please go to your User Settings to connect your GitLab account."
                          : ""
                      }
                    >
                      <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center h-14 bg-background-secondary hover:bg-background-tertiary border-border-secondary w-full"
                        onClick={() => handleCreateRepoClick("gitlab")}
                        disabled={!isGitlabConnected}
                      >
                        <img
                          src="/icons/gitlab-logo.svg"
                          className="w-6 h-6 mb-1"
                          alt="GitLab"
                        />
                        <span className="text-text-primary text-xs">
                          GitLab
                        </span>
                      </Button>
                    </span>
                    <span
                      data-tooltip-id="tooltip"
                      data-tooltip-html={
                        !isBitbucketConnected
                          ? "A Bitbucket account is required to create a repository.<br /><br />Please go to your User Settings to connect your Bitbucket account."
                          : ""
                      }
                    >
                      <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center h-14 bg-background-secondary hover:bg-background-tertiary border-border-secondary w-full"
                        onClick={() => handleCreateRepoClick("bitbucket")}
                        disabled={!isBitbucketConnected}
                      >
                        <img
                          src="/icons/bitbucket-logo.svg"
                          className="w-6 h-6 mb-1"
                          alt="Bitbucket"
                        />
                        <span className="text-text-primary text-xs">
                          Bitbucket
                        </span>
                      </Button>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NoRepositoryModal;
